import store from "@/store/store.js"
import {sleep} from "@/utils/sleep.js"
import {settings} from '@/configs/settings.js'
import { trainingProgramCache } from '@/utils/trainingProgramCache.js'

const wsBroadcast = {
  isWorking: false,
  forcedClose: false,
  start,
  stop,
  markAiGenerationSent,
  clearAiGenerationDebug,
}

export { wsBroadcast }

let wsUserInfo = null
let aiGenerationDebugSentAt = null
let aiGenerationDebugMsgIndex = 0

function markAiGenerationSent() {
  aiGenerationDebugSentAt = performance.now()
  aiGenerationDebugMsgIndex = 0
  console.log(
    '[AI gen debug] HTTP generation request sent',
    new Date().toISOString(),
  )
}

function clearAiGenerationDebug() {
  aiGenerationDebugSentAt = null
  aiGenerationDebugMsgIndex = 0
}

function isTerminalGenerationStatus(status) {
  return status === 'completed'
    || status === 'generation_failed'
    || status === 'failed'
}

function canConnectUserSocket() {
  return Boolean(store.state.user.token && store.state.user.profile?.id)
}

function stop() {
  wsBroadcast.forcedClose = true
  wsBroadcast.isWorking = false

  try {
    wsUserInfo?.close()
  } catch (e) {
    console.log('close', e)
  }

  wsUserInfo = null
}

function start() {
  if (wsBroadcast.isWorking || !canConnectUserSocket()) {
    return
  }

  wsBroadcast.forcedClose = false
  wsBroadcast.isWorking = true

  const userInfoUrl = new URL(`${settings.env.VUE_APP_WS_URL}/ws/user/${store.state.user.profile.id}/`)
  userInfoUrl.searchParams.set('token', store.state.user.token)
  wsUserInfo = new WebSocket(userInfoUrl.toString())

  wsUserInfo.onopen = async () => {
    // state.wsUserInfo.send({})
  }

  wsUserInfo.onerror = async (e) => {
    console.log('error', e)
    try {
      wsUserInfo?.close()
    } catch (err) {
      console.error(err)
    }
    wsBroadcast.isWorking = false
    await sleep(1e3)
    if (!wsBroadcast.forcedClose && canConnectUserSocket()) {
      start()
    }
  }

  wsUserInfo.onclose = async (_e) => {
    wsBroadcast.isWorking = false
    await sleep(1e3)
    if (!wsBroadcast.forcedClose && canConnectUserSocket()) {
      start()
    }
  }

  wsUserInfo.onmessage = message => {
    const json = JSON.parse(message.data)

    if (aiGenerationDebugSentAt != null) {
      aiGenerationDebugMsgIndex += 1
      const elapsedMs = Math.round(performance.now() - aiGenerationDebugSentAt)
      const generation = json.user?.generated_progresses?.[0]
      const status = generation?.status
      const hasGeneratedImg = Boolean(generation?.generated_img)

      console.log(
        '[AI gen debug] WS /ws/user response',
        {
          n: aiGenerationDebugMsgIndex,
          elapsedMs,
          at: new Date().toISOString(),
          status,
          hasGeneratedImg,
          generatedImg: generation?.generated_img || null,
          generationError: generation?.generation_error,
        },
      )

      if (isTerminalGenerationStatus(status) || hasGeneratedImg) {
        console.log(
          '[AI gen debug] generation finished (by WS)',
          { elapsedMs, status, hasGeneratedImg },
        )
        clearAiGenerationDebug()
      }
    }

    store.commit('user/setProfile', json.user)
  }
}

const wsProgramBroadcast = {
  isActive: false,
  programId: null,
  startProgramBroadcast,
  stopProgramBroadcast,
  forcedClose: false
}

export { wsProgramBroadcast }

let wsProgramInfo = null

function stopProgramBroadcast() {
  if (wsProgramInfo) {
    wsProgramBroadcast.forcedClose = true; // Указываем, что закрываем намеренно
    wsProgramInfo.close();
    wsProgramInfo = null;
    store.commit('user/setCurrentTrainingProgram', null)
  }
  wsProgramBroadcast.isActive = false;
  wsProgramBroadcast.programId = null;
}

function startProgramBroadcast(programId, { restoreCache = false } = {}) {
  if (!programId) return console.error("programId is required");

  if (wsProgramBroadcast.isActive) {
    if (String(wsProgramBroadcast.programId) === String(programId)) {
      if (restoreCache && !store.state.user.currentTrainingProgram) {
        const userId = store.state.user.profile?.id

        void trainingProgramCache.get(userId, programId).then(cachedProgram => {
          const isSameProgramActive = wsProgramBroadcast.isActive
            && String(wsProgramBroadcast.programId) === String(programId)

          if (
            cachedProgram
            && isSameProgramActive
            && !store.state.user.currentTrainingProgram
          ) {
            store.commit('user/setCurrentTrainingProgram', cachedProgram)
          }
        })
      }
      return
    }
    stopProgramBroadcast()
  }

  wsProgramBroadcast.isActive = true;
  wsProgramBroadcast.forcedClose = false;
  wsProgramBroadcast.programId = programId;

  const userId = store.state.user.profile?.id
  let hasFreshMessage = false
  const url = new URL(`${settings.env.VUE_APP_WS_URL}/ws/user_training_program/${programId}/`)
  url.searchParams.set('token', store.state.user.token)
  const currentWsProgramInfo = new WebSocket(url.toString());
  wsProgramInfo = currentWsProgramInfo

  if (restoreCache) {
    void trainingProgramCache.get(userId, programId).then(cachedProgram => {
      const isCurrentConnection = wsProgramInfo === currentWsProgramInfo
        && wsProgramBroadcast.isActive
        && String(wsProgramBroadcast.programId) === String(programId)

      if (cachedProgram && !hasFreshMessage && isCurrentConnection) {
        store.commit('user/setCurrentTrainingProgram', cachedProgram)
      }
    })
  }

  currentWsProgramInfo.onmessage = message => {
    const json = JSON.parse(message.data)
    const program = json.user_training_program
    hasFreshMessage = true
    console.log('WS Program: Message received', json);
    store.commit('user/setCurrentTrainingProgram', program)
    void trainingProgramCache.set(userId, programId, program)
  }

  wsProgramInfo.onerror = async (e) => {
    console.log('error', e)
    try {
      wsProgramInfo.close()
    } catch (e) {
      console.error(e)
    }
    const reconnectProgramId = wsProgramBroadcast.programId
    wsProgramBroadcast.isActive = false
    await sleep(1e3)
    if (
      reconnectProgramId
      && !wsProgramBroadcast.forcedClose
      && store.state.user.token
    ) {
      startProgramBroadcast(reconnectProgramId)
    }
  }
}
